"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Plus, X, Search, Edit2, Trash2, Tag, CheckCircle,
  XCircle, ToggleLeft, ToggleRight, Layers, ShieldCheck, Ban,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useAssetCategories } from "../../context/AssetCategoryContext";
import { useAssetType } from "../../context/AssetTypeContext";
import { useAuth } from "../../context/AuthContext";

// ─── Shared style tokens (same as AssetManagementModule) ─────────────────
const gradientStyle = { background: "linear-gradient(135deg, #2563eb, #d12465)" };
const gradientBtn =
  "text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90";

const statusBadge = (active) =>
  active
    ? "px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap"
    : "px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 whitespace-nowrap";

// ─── Empty form ───────────────────────────────────────────────────────────
const emptyForm = () => ({ name: "", category: "", active: true });

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function AssetTypesModule() {
  const { categories } = useAssetCategories();
  const { assetTypes, fetchAssetTypes, loading, createAssetType, updateAssetType, updateAssetTypeStatus, deleteAssetType, setAssetTypes} = useAssetType();
  const { canDo } = useAuth();

  useEffect(() => {
    fetchAssetTypes();
  }, []);

const getUserId = () => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return null;
  try {
    const user = JSON.parse(storedUser);
    return user?.id || null;
  } catch (e) {
    return null;
  }
};

const isActive = (status) => status?.trim().toLowerCase() === "active";

  const [search, setSearch]       = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // modals
  const [addModal, setAddModal]       = useState(false);
  const [editModal, setEditModal]     = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal]     = useState(false);
  const [selected, setSelected]       = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});

  // ── derived stats ──────────────────────────────────────────────────────
  // const totalTypes  = types.length;
  // const activeTypes = types.filter(t => t.status === "Active").length;

  const totalTypes = assetTypes?.length || 0;


const activeTypes = assetTypes?.filter(t => isActive(t.status)).length || 0;
const inactiveTypes = assetTypes?.filter(t => !isActive(t.status)).length || 0;

  // const inactiveTypes = types.filter(t => !t.status === "Active").length;

  // ── filtered list ──────────────────────────────────────────────────────
const filtered = useMemo(() => assetTypes?.filter(t => {
  const matchCat =
    catFilter === "All" || (t.category_name || t.category) === catFilter;

  const matchStatus =
    statusFilter === "All" ||
    (statusFilter === "Active"
      ? isActive(t.status)
      : !isActive(t.status));

  const matchSearch =
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    (t.category_name || t.category || "").toLowerCase().includes(search.toLowerCase());

  return matchCat && matchStatus && matchSearch;
}) || [], [assetTypes, search, catFilter, statusFilter]);

  // ── pagination ─────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  useEffect(() => { setPage(1); }, [search, catFilter, statusFilter]);
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(startIndex, startIndex + rowsPerPage);

  // ── validation ─────────────────────────────────────────────────────────
  const validate = (f, isEdit = false) => {
    const e = {};
    if (!f.name.trim()) e.name = "Type name is required";
    else if (!isEdit && assetTypes.find(t => t.name.toLowerCase() === f.name.trim().toLowerCase()))
      e.name = "A type with this name already exists";
    return e;
  };

  // ── handlers ───────────────────────────────────────────────────────────
  const openAdd = () => { setForm(emptyForm()); setErrors({}); setAddModal(true); };

  const handleAdd = async (e) => {
  e.preventDefault();

  const errs = validate(form);
  if (Object.keys(errs).length) return setErrors(errs);

  const userId = getUserId();

  await createAssetType({
  name: form.name.trim(),
  category_id: form.category,
  status: form.active ? "Active" : "Inactive",
  create_audit_id: userId
});

  setAddModal(false);
};

const openEdit = (t) => {
  setSelected(t);
  setForm({ name: t.name, category: t.category_id, active: isActive(t.status) });
  setErrors({});
  setEditModal(true);
};

  const handleEdit = async (e) => {
  e.preventDefault();

  const errs = validate(form, true);
  if (Object.keys(errs).length) return setErrors(errs);

  const userId = getUserId();

  await updateAssetType(selected.id, {
  name: form.name.trim(),
  category_id: form.category,
  status: form.active ? "Active" : "Inactive",
  update_audit_id: userId
});

  setEditModal(false);
};


 const toggleStatus = async (t) => {
  const newStatus = isActive(t.status) ? "Inactive" : "Active";

  // 1. instant UI update
  setAssetTypes(prev =>
    prev.map(item =>
      item.id === t.id
        ? { ...item, status: newStatus }
        : item
    )
  );

  // 2. API call
  const res = await updateAssetTypeStatus(t.id, {
    status: newStatus,
    update_audit_id: getUserId()
  });

  // 3. rollback if failed
  if (!res?.success) {
    setAssetTypes(prev =>
      prev.map(item =>
        item.id === t.id
          ? { ...item, status: t.status }
          : item
      )
    );
  }
};

  const confirmDelete = (t) => { setSelected(t); setDeleteModal(true); };
    const handleDelete = async () => {
    const userId = getUserId();

    await deleteAssetType(selected.id, userId);

    setDeleteModal(false);
  };

  const openView = (t) => { setSelected(t); setViewModal(true); };

  // ── category pill colour ───────────────────────────────────────────────
  const catColor = {
    Hardware:      "bg-blue-50 text-blue-700",
    Peripherals:   "bg-sky-50 text-sky-700",
    Accessory:     "bg-orange-50 text-orange-700",
    Identity:      "bg-pink-50 text-pink-700",
    Communication: "bg-teal-50 text-teal-700",
    Other:         "bg-gray-100 text-gray-600",
  };

  // ── shared field styles ────────────────────────────────────────────────
  const input = (err) =>
    `border ${err ? "border-red-400" : "border-gray-300"} rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300`;

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-9 py-6">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Asset Types</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Define and manage the categories of assets your organisation tracks
          </p>
        </div>

        {/* ── Stats ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {[
            { label: "Total Types",    value: totalTypes,    icon: <Layers size={18}/>,     color: "text-blue-600 bg-blue-50" },
            { label: "Active Types",   value: activeTypes,   icon: <ShieldCheck size={18}/>, color: "text-green-600 bg-green-50" },
            { label: "Inactive Types", value: inactiveTypes, icon: <Ban size={18}/>,         color: "text-gray-500 bg-gray-100" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
              <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="text-lg font-bold text-gray-800">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Toolbar ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2 mb-4 shadow-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
            <input
              type="text"
              placeholder="Search by name, category"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <select
  value={catFilter}
  onChange={e => setCatFilter(e.target.value)}
  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-blue-300"
>
  <option value="All">All</option>
  {categories?.filter(c => c.status === "Active") .map(c => (
    <option key={c.id} value={c.name}>{c.name}</option>
  ))}
</select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-36 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="All">All</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>

          {(search || catFilter !== "All" || statusFilter !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setCatFilter("All");
                setStatusFilter("All");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}

{canDo("Asset Types", "Add") && (
          <button
            onClick={openAdd}
            className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto`}
            style={gradientStyle}
          >
            <Plus size={16}/> Add Asset Type
          </button>
)}
        </div>

        {/* ── Desktop Table ───────────────────────────────────────────── */}
        <div className="hidden md:block bg-white shadow-md rounded-2xl p-4">
          <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-sky-50 text-gray-600">
              <tr>
                {["Id", "Type Name", "Category", "Status", ...(canDo("Asset Types", "Edit") ||
  canDo("Asset Types", "Delete")
    ? ["Actions"]
    : []),].map(h => (
                  <th key={h} className="p-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((t, idx) => (
                <tr key={t.id} className={`border-b hover:bg-gray-50 transition-colors ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                  <td className="p-3 text-gray-400 text-xs">{startIndex + idx + 1}</td>
                  <td className="p-3 text-gray-800 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 rounded-md bg-gradient-to-br from-blue-500 to-pink-500 text-white">
                        <Tag size={12}/>
                      </span>
                      {t.name}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catColor[t.category_name || t.category] || "bg-gray-100 text-gray-600"}`}>
                      {t.category_name || t.category}
                    </span>
                  </td>
                  <td className="p-3">
                    <span
  title={isActive(t.status) ? "Deactivate" : "Activate"}
  onClick={() => toggleStatus(t)}
  className={`${statusBadge(isActive(t.status))} cursor-pointer`}
>
  {isActive(t.status) ? "Active" : "Inactive"}
</span>
                  </td>
                  <td className="p-3">
                    {(canDo("Asset Types", "Edit") ||
  canDo("Asset Types", "Delete")) && (
                    <div className="flex gap-3 items-center">
                     {canDo("Asset Types", "Edit") && (
                      <button title="Edit" className="text-blue-600 hover:text-blue-800" onClick={() => openEdit(t)}>
                        <Edit2 size={15}/>
                      </button>
                     )}
                     {canDo("Asset Types", "Delete") && (
                      <button
  title="Delete"
  className={`${(t.usageCount > 0) ? "text-gray-300 cursor-not-allowed" : "text-red-500 hover:text-red-700"}`}
  onClick={() => !(t.usageCount > 0) && confirmDelete(t)}
  disabled={t.usageCount > 0}
>
  <Trash2 size={15}/>
</button>
                     )}
                    </div>
  )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    <Tag size={32} className="mx-auto mb-2 opacity-30"/>
                    No asset types match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <div>
            <Pagination page={currentPage} setPage={setPage} totalPages={totalPages} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
          </div>
        </div>

        {/* ── Mobile Cards ────────────────────────────────────────────── */}
        <div className="md:hidden space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-gray-400 bg-white rounded-xl">No asset types found</div>
          )}
          {currentRows.map(t => (
            <div key={t.id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded-md bg-gradient-to-br from-blue-500 to-pink-500 text-white">
                    <Tag size={12}/>
                  </span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{t.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catColor[t.category_name || t.category] || "bg-gray-100 text-gray-600"}`}>
                      {t.category_name || t.category}
                    </span>
                  </div>
                </div>
                <span className={statusBadge(isActive(t.status))}>{isActive(t.status) ? "Active" : "Inactive"}</span>

              </div>
              <div className="flex items-center justify-between mt-1">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.usageCount > 0 ? "bg-blue-50 text-blue-700" : "bg-gray-50 text-gray-400"}`}>
                  {t.usageCount} asset{t.usageCount !== 1 ? "s" : ""}
                </span>
                <div className="flex gap-3 items-center">
                  <button className="text-blue-600" onClick={() => openEdit(t)}><Edit2 size={15}/></button>
                  <button
  title="Delete"
  className={`${(t.usageCount > 0) ? "text-gray-300 cursor-not-allowed" : "text-red-500 hover:text-red-700"}`}
  onClick={() => !(t.usageCount > 0) && confirmDelete(t)}
  disabled={t.usageCount > 0}
>
  <Trash2 size={15}/>
</button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm px-4 pb-4">
              <Pagination page={currentPage} setPage={setPage} totalPages={totalPages} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
            </div>
          )}
        </div>

        {/* ── Footer note ─────────────────────────────────────────────── */}
        {filtered.some(t => t.usageCount > 0) && (
          <p className="text-xs text-gray-400 mt-3 text-right">
            * Types with assigned assets cannot be deleted — reassign or remove assets first
          </p>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          ADD MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {addModal && (
        <Modal title="Add Asset Type" onClose={() => setAddModal(false)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <Field label="Type Name *" error={errors.name}>
              <input
                type="text"
                placeholder="e.g. Docking Station"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={input(errors.name)}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </Field>

            <Field label="Category">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={input(false)}>
                <option value="">Select Category</option>
                {categories?.filter(c => c.status === "Active") .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Status">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                    form.active
                      ? "border-green-400 bg-green-50 text-green-700"
                      : "border-gray-300 bg-gray-50 text-gray-500"
                  }`}
                >
                  {form.active ? <CheckCircle size={15}/> : <XCircle size={15}/>}
                  {form.active ? "Active" : "Inactive"}
                </button>
                <span className="text-xs text-gray-400">Toggle to change status</span>
              </div>
            </Field>

            <ModalActions
              onCancel={() => setAddModal(false)}
              submitLabel="Add Type"
            />
          </form>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          EDIT MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {editModal && selected && (
        <Modal title={`Edit — ${selected.name}`} onClose={() => setEditModal(false)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <Field label="Type Name *" error={errors.name}>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={input(errors.name)}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </Field>

            <Field label="Category">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={input(false)}>
                <option value="">Select Category</option>
                {categories?.filter(c => c.status === "Active") .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Status">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                    form.active
                      ? "border-green-400 bg-green-50 text-green-700"
                      : "border-gray-300 bg-gray-50 text-gray-500"
                  }`}
                >
                  {form.active ? <CheckCircle size={15}/> : <XCircle size={15}/>}
                  {form.active ? "Active" : "Inactive"}
                </button>
                <span className="text-xs text-gray-400">Toggle to change status</span>
              </div>
            </Field>

            {selected.usageCount > 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                ⚠ This type is used by {selected.usageCount} asset(s). The name change will reflect across the inventory.
              </p>
            )}

            <ModalActions onCancel={() => setEditModal(false)} submitLabel="Save Changes" />
          </form>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DELETE CONFIRMATION
      ══════════════════════════════════════════════════════════════════ */}
      {deleteModal && selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 w-full sm:max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Asset Type</h3>
            <p className="text-sm text-gray-500 mb-5">
              Permanently remove the <strong>"{selected.name}"</strong> type? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tiny shared sub-components ──────────────────────────────────────────
// Pagination — same look as the Letters page
const navBtn = "px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

function Pagination({ page, setPage, totalPages, rowsPerPage, setRowsPerPage }) {
  const start = Math.max(page - 1, 1);
  const end = Math.min(start + 2, totalPages);
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 transition"
          value={rowsPerPage}
          onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
        >
          {[5, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center space-x-1">
        <button onClick={() => setPage(1)} disabled={page === 1} className={navBtn}><ChevronsLeft size={16} /></button>
        <button onClick={() => setPage(Math.max(page - 1, 1))} disabled={page === 1} className={navBtn}><MdKeyboardArrowLeft size={16} /></button>
        {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(n => (
          <button key={n} onClick={() => setPage(n)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${n === page ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            style={n === page ? gradientStyle : undefined}
          >{n}</button>
        ))}
        <button onClick={() => setPage(Math.min(page + 1, totalPages))} disabled={page === totalPages} className={navBtn}><MdKeyboardArrowRight size={16} /></button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className={navBtn}><ChevronsRight size={16} /></button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose}><X size={20} className="text-gray-500"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onCancel, submitLabel }) {
  return (
    <div className="flex justify-end gap-3 pt-1">
      <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
        Cancel
      </button>
      <button type="submit" className={gradientBtn} style={gradientStyle}>
        {submitLabel}
      </button>
    </div>
  );
}